import angular from "angular";
import {
  IExpressionFunction,
  IFieldRuntimeObject,
  ITemplateOptions,
  ITemplateScope,
  ITemplateScopeOptions,
  IValidator,
  IWatcher,
} from "AngularFormly";
import { LookupRecord, Record } from "../../../../server/crud";
import { IQuery } from "../../../../server/query";
import {
  ServerUtility,
  vsName as ServerUtilityName,
} from "../../../../server/serverUtility";
import { orderBy } from "lodash";
import { or } from "@uirouter/angularjs";

export interface DefaultItem extends Record {
  Name: string;
}

export interface RecordSelectAsyncTemplateOptions extends ITemplateOptions {
  queryUrl: string;
  queryBuilder?: QueryBuilder;
  getDefaultItem: (model: any) => DefaultItem | undefined;
  serverItemMapper?: (model: any) => DefaultItem;
  useWrappedQuery?: boolean;
  model?: string | object;
  expressionProperties?: {
    [key: string]: string | IExpressionFunction | IValidator;
  };
  hideExpression?: string | IExpressionFunction;
  watcher?: IWatcher | IWatcher[] | undefined;
  customDatasource?: RecordSelectAsyncDatasource;
  disableDefaultQueryCaching?: boolean;
  onSelect?: (
    selectedItem: unknown,
    model: unknown,
    field: ITemplateScopeOptions
  ) => void;
  className?: string;
  helpText?: (model: any) => string;
}

export interface QueryBuilder {
  (search: string, model: Record): ISelectAsyncQuery;
}

interface RecordSelectAsyncControllerScope extends ITemplateScope {
  to: RecordSelectAsyncTemplateOptions;
  model: Record;
  currentItems: Record[];
  valueProp: string;
  labelProp: string;
  formState: any;
  options: ITemplateScopeOptions;
  onSelect: (selected: any) => void;
  query: (search: string, model: Record) => void;
  selectedItem: {} | undefined;
}

export interface ISelectAsyncQuery extends IQuery {
  NameContains?: string;
  Disabled?: boolean;
}

export interface RecordSelectAsyncDatasource {
  queryItems: (query: ISelectAsyncQuery) => angular.IPromise<Record[]>;
}

export class RecordSelectAsyncDatasourceServerQueryDatasource
  implements RecordSelectAsyncDatasource
{
  constructor(
    private readonly serverUtility: ServerUtility,
    private readonly queryUrl: string
  ) {}

  queryItems(query: ISelectAsyncQuery): angular.IPromise<Record[]> {
    return this.serverUtility
      .query<Record>({ url: this.queryUrl, query })
      .then((r) => r.Results);
  }
}

export class RecordSelectAsyncDatasourceWrappedSeverQueryDataSource
  implements RecordSelectAsyncDatasource
{
  constructor(
    private readonly serverUtility: ServerUtility,
    private readonly queryUrl: string
  ) {}

  queryItems(query: ISelectAsyncQuery): angular.IPromise<Record[]> {
    return this.serverUtility
      .queryWrapped<Record>({ url: this.queryUrl, query })
      .then((r) => r.Results);
  }
}

export class RecordSelectAsyncDatasourceCachedDataSource
  implements RecordSelectAsyncDatasource
{
  constructor(private readonly itemPromise: angular.IPromise<LookupRecord[]>) {}
  queryItems(query: ISelectAsyncQuery): angular.IPromise<Record[]> {
    return this.itemPromise.then((items) => {
      let matchingItems = [...items];

      if (query.NameContains) {
        return matchingItems.filter((item) =>
          item.Name.toLowerCase().includes(query.NameContains!.toLowerCase())
        );
      }
      if (query.Disabled !== undefined) {
        matchingItems = matchingItems.filter(
          (item) => item.Disabled === query.Disabled
        );
      }

      if (query.OrderBy) {
        const orderByParts = query.OrderBy.split(",");
        orderBy(
          matchingItems,
          orderByParts.map((p) => p.replace("-", "")),
          orderByParts.map((p) => (p.startsWith("-") ? "desc" : "asc"))
        );
      }

      let skip = query.Skip ?? 0;
      let take = query.Take ?? matchingItems.length;

      return matchingItems.slice(skip, skip + take);
    });
  }
}

export class RecordSelectAsyncController {
  static $inject = ["$scope", ServerUtilityName];
  defaultQueryPromise: angular.IPromise<Record[]> | undefined;
  private readonly queryBuilder: QueryBuilder;
  private readonly dataSource: RecordSelectAsyncDatasource;

  get templateOptions(): RecordSelectAsyncTemplateOptions {
    return this.$scope.to;
  }

  constructor(
    private readonly $scope: RecordSelectAsyncControllerScope,
    serverUtility: ServerUtility
  ) {
    $scope.currentItems = [];
    $scope.valueProp ??= "Id";
    $scope.labelProp ??= $scope.to.labelProp ?? "Name";
    $scope.onSelect = (selected) => this.onSelect(selected);
    $scope.query = (search) => this.query(search);
    this.queryBuilder =
      this.templateOptions.queryBuilder ??
      RecordSelectAsyncController.defaultQueryBuilder;

    const { useWrappedQuery, queryUrl, customDatasource } =
      this.templateOptions;
    if (customDatasource) {
      this.dataSource = customDatasource;
    } else if (useWrappedQuery) {
      this.dataSource =
        new RecordSelectAsyncDatasourceWrappedSeverQueryDataSource(
          serverUtility,
          queryUrl
        );
    } else {
      this.dataSource = new RecordSelectAsyncDatasourceServerQueryDatasource(
        serverUtility,
        queryUrl
      );
    }

    this.$onInit();
  }
  $onInit() {
    const model = this.$scope.model;
    const initialItem = this.$scope.model
      ? this.templateOptions.getDefaultItem(this.$scope.model)
      : undefined;
    this.$scope.selectedItem = initialItem;
    const disableDefaultQueryCaching =
      this.templateOptions.disableDefaultQueryCaching ?? false;
    if (!disableDefaultQueryCaching) {
      this.defaultQueryPromise = this.getResultsFromServer(
        this.queryBuilder("", model)
      );
    }

    if (initialItem === undefined) {
      return;
    }
    this.$scope.currentItems = [initialItem];
  }

  onSelect(selected: any) {
    this.$scope.selectedItem = selected;
    this.templateOptions.onSelect?.call(
      this,
      selected,
      this.$scope.model,
      this.$scope.options
    );
  }

  query(search: string) {
    if (
      !search &&
      !(this.templateOptions.disableDefaultQueryCaching ?? false)
    ) {
      this.defaultQueryPromise?.then((r) => (this.$scope.currentItems = r));
      return;
    }
    const query = this.queryBuilder(search, this.$scope.model);
    this.getResultsFromServer(query);
  }

  private static DefaultServerItemMapper(item: any): DefaultItem {
    return {
      ...item,
    };
  }

  private getResultsFromServer(
    query: ISelectAsyncQuery
  ): angular.IPromise<Record[]> {
    query.Skip = 0;
    query.Take = 10;
    query.OrderBy ??= this.$scope.labelProp;

    const mapper =
      this.templateOptions.serverItemMapper ??
      RecordSelectAsyncController.DefaultServerItemMapper;

    return this.dataSource
      .queryItems(query)
      .then((results) => results.map(mapper))
      .then((results) => (this.$scope.currentItems = results));
  }

  private static defaultQueryBuilder(search: string): ISelectAsyncQuery {
    return {
      NameContains: search,
    };
  }
}
